import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Empty,
  InputNumber,
  Pagination,
  Select,
  Skeleton,
  Space,
  Switch,
  Tabs,
  Tag,
  TimePicker,
  Tooltip,
  message,
} from "antd";
import {
  ClockCircleOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchWaitingPriceReportConfig,
  fetchWaitingPriceReportJobs,
  fetchWaitingPriceReportUsers,
  fetchWaitingPriceReportUserTypes,
  saveWaitingPriceReportConfig,
} from "../qv4/models/shared/waitingPriceReportModel";

const DEFAULT_OWNER_USER_TYPES = ["Sales Person", "Sales Manager", "Sales Trainee", "Operations"];

const DEFAULT_CONFIG = {
  enabled: false,
  allowedOwnerUserTypes: DEFAULT_OWNER_USER_TYPES,
  salesUserIds: [],
  purchaserUserIds: [],
  times: [],
  intervalMinutes: 5,
  formats: ["pdf"],
};

const JOB_STATUS = {
  pending: { label: "Pending", color: "default" },
  retry: { label: "Retrying", color: "warning" },
  processing: { label: "Processing", color: "processing" },
  completed: { label: "Completed", color: "success" },
  partially_failed: { label: "Partially failed", color: "warning" },
  failed: { label: "Failed", color: "error" },
  skipped: { label: "Skipped", color: "default" },
  cancelled: { label: "Cancelled", color: "default" },
};

const JOB_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  ...Object.entries(JOB_STATUS).map(([value, item]) => ({ value, label: item.label })),
];

function timeValue(value) {
  const [hour, minute] = String(value || "00:00").split(":").map(Number);
  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function nextAvailableTime(times) {
  const used = new Set(times);
  for (let hour = 9; hour < 24; hour += 1) {
    const candidate = `${String(hour).padStart(2, "0")}:00`;
    if (!used.has(candidate)) return candidate;
  }
  return "00:00";
}

function validateConfig(config) {
  if (!Number.isInteger(config.intervalMinutes) || config.intervalMinutes < 1 || config.intervalMinutes > 120) {
    return "Interval must be between 1 and 120 minutes.";
  }
  if (!config.enabled) return null;
  if (!config.allowedOwnerUserTypes.length) return "Select at least one report-owner type.";
  if (!config.salesUserIds.length) return "Select at least one report owner.";
  if (!config.purchaserUserIds.length) return "Select at least one Purchaser.";
  if (!config.times.length) return "Add at least one schedule time.";
  if (new Set(config.times).size !== config.times.length) return "Schedule times must be unique.";
  if (!config.formats.length) return "Select PDF, Excel, or both.";
  return null;
}

function hasUnavailableWatiCredentials(user) {
  return user.hasUsableWatiCredentials === false || Number(user.hasUsableWatiCredentials) === 0;
}

function canonicalizeOwnerTypes(configuredTypes, registeredTypes) {
  const exactTypes = new Set(registeredTypes);
  const byLowerCase = new Map(registeredTypes.map(userType => [userType.toLowerCase(), userType]));
  return [...new Set((configuredTypes || [])
    .map(userType => {
      const value = String(userType).trim();
      return exactTypes.has(value) ? value : byLowerCase.get(value.toLowerCase());
    })
    .filter(userType => userType && userType !== "Purchaser"))];
}

function formatScheduledTime(value) {
  if (!value) return "Unknown time";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dubai",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(value));
}

function UserOption({ user, showCredential = false }) {
  const fullName = [user.name, user.surname].filter(Boolean).join(" ") || user.username || `User ${user.id}`;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{fullName}</span>
      <span style={{ color: "#8c8c8c", fontSize: 11, flexShrink: 0 }}>
        {showCredential && !user.hasUsableWatiCredentials ? "WATI unavailable" : user.user_type}
      </span>
    </div>
  );
}

function ConfigurationPane({
  config,
  setConfig,
  salesUsers,
  purchasers,
  availableOwnerTypes,
  usersLoading,
  saving,
  onSave,
  onAllowedTypesChange,
}) {
  const validationError = validateConfig(config);
  const unavailableSalesUsers = salesUsers.filter(hasUnavailableWatiCredentials);
  const salesOptions = salesUsers.map(user => ({
    value: user.id,
    label: <UserOption user={user} showCredential />,
    searchLabel: [user.name, user.surname, user.username, user.user_type].filter(Boolean).join(" "),
    disabled: hasUnavailableWatiCredentials(user) && !config.salesUserIds.includes(user.id),
  }));
  const purchaserOptions = purchasers.map(user => ({
    value: user.id,
    label: <UserOption user={user} />,
    searchLabel: [user.name, user.surname, user.username, user.telephone].filter(Boolean).join(" "),
  }));

  const updateTime = (index, value) => {
    if (!value) return;
    setConfig(current => ({
      ...current,
      times: current.times.map((time, itemIndex) => itemIndex === index ? value.format("HH:mm") : time),
    }));
  };

  const removeTime = (index) => {
    setConfig(current => ({
      ...current,
      times: current.times.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  return (
    <div style={{ height: 370, overflowY: "auto", paddingRight: 4 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Automated reports</div>
          <div style={{ color: "#8c8c8c", fontSize: 12 }}>Timezone: Asia/Dubai</div>
        </div>
        <Switch
          checked={config.enabled}
          checkedChildren="Enabled"
          unCheckedChildren="Disabled"
          onChange={enabled => setConfig(current => ({ ...current, enabled }))}
        />
      </div>

      <Divider style={{ margin: "0 0 12px" }} />

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Allowed report-owner types</div>
        <Select
          mode="multiple"
          value={config.allowedOwnerUserTypes}
          options={availableOwnerTypes.map(userType => ({ value: userType, label: userType }))}
          optionFilterProp="label"
          placeholder="Select permitted user types"
          maxTagCount="responsive"
          style={{ width: "100%" }}
          onChange={onAllowedTypesChange}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Sales / Operations users</div>
          <Select
            mode="multiple"
            value={config.salesUserIds}
            options={salesOptions}
            optionFilterProp="searchLabel"
            placeholder="Select Sales / Operations users"
            maxTagCount="responsive"
            loading={usersLoading}
            style={{ width: "100%" }}
            onChange={salesUserIds => setConfig(current => ({ ...current, salesUserIds }))}
          />
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Purchaser recipients</div>
          <Select
            mode="multiple"
            value={config.purchaserUserIds}
            options={purchaserOptions}
            optionFilterProp="searchLabel"
            placeholder="Select Purchasers"
            maxTagCount="responsive"
            loading={usersLoading}
            style={{ width: "100%" }}
            onChange={purchaserUserIds => setConfig(current => ({ ...current, purchaserUserIds }))}
          />
        </div>
      </div>

      {unavailableSalesUsers.length > 0 && (
        <div style={{ marginTop: 7, color: "#8c8c8c", fontSize: 11 }}>
          {unavailableSalesUsers.map(user => (
            <span key={user.id} style={{ marginRight: 12 }}>
              {[user.name, user.surname].filter(Boolean).join(" ") || user.username}: <b>WATI unavailable</b>
            </span>
          ))}
        </div>
      )}

      <Divider style={{ margin: "14px 0 12px" }} />

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 175px", gap: 18 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Daily times</div>
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setConfig(current => ({
                ...current,
                times: [...current.times, nextAvailableTime(current.times)],
              }))}
            >
              Add time
            </Button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 7 }}>
            {config.times.map((time, index) => (
              <div key={`${index}-${time}`} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <TimePicker
                  aria-label={`Schedule time ${index + 1}`}
                  value={timeValue(time)}
                  format="HH:mm"
                  minuteStep={5}
                  allowClear={false}
                  onChange={value => updateTime(index, value)}
                  style={{ width: "100%" }}
                  suffixIcon={<ClockCircleOutlined />}
                />
                <Tooltip title="Remove time">
                  <Button
                    aria-label={`Remove schedule time ${index + 1}`}
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeTime(index)}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
          {!config.times.length && (
            <div style={{ color: "#8c8c8c", fontSize: 12, padding: "7px 0" }}>No schedule times</div>
          )}
        </div>

        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 7 }}>Agent interval</div>
          <Space.Compact style={{ width: "100%" }}>
            <InputNumber
              min={1}
              max={120}
              precision={0}
              value={config.intervalMinutes}
              style={{ width: "100%" }}
              onChange={value => setConfig(current => ({ ...current, intervalMinutes: value }))}
            />
            <Button disabled style={{ width: 70 }}>minutes</Button>
          </Space.Compact>
        </div>
      </div>

      <Divider style={{ margin: "14px 0 12px" }} />

      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 7 }}>Report formats</div>
          <Checkbox.Group
            value={config.formats}
            onChange={formats => setConfig(current => ({ ...current, formats }))}
          >
            <Checkbox value="pdf"><FilePdfOutlined style={{ color: "#cf1322" }} /> PDF</Checkbox>
            <Checkbox value="excel"><FileExcelOutlined style={{ color: "#237804" }} /> Excel</Checkbox>
          </Checkbox.Group>
        </div>
        <div style={{ textAlign: "right" }}>
          {validationError && config.enabled && (
            <div style={{ color: "#cf1322", fontSize: 11, marginBottom: 5 }}>{validationError}</div>
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={Boolean(validationError)}
            onClick={onSave}
          >
            Save schedule
          </Button>
        </div>
      </div>
    </div>
  );
}

function JobsPane({
  jobs,
  pagination,
  loading,
  status,
  setStatus,
  onLoad,
}) {
  return (
    <div style={{ height: 370, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
        <Select
          aria-label="Job status"
          size="small"
          value={status}
          options={JOB_FILTER_OPTIONS}
          style={{ width: 170 }}
          onChange={nextStatus => {
            setStatus(nextStatus);
            onLoad(1, nextStatus);
          }}
        />
        <Tooltip title="Refresh jobs">
          <Button
            aria-label="Refresh jobs"
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => onLoad(pagination.page || 1, status)}
          />
        </Tooltip>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", borderTop: "1px solid #f0f0f0" }}>
        {!loading && !jobs.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No report jobs" />
        ) : jobs.map(job => {
          const statusInfo = JOB_STATUS[job.status] || { label: job.status || "Unknown", color: "default" };
          const successful = (job.delivery_results || []).filter(item => item.success).length;
          const total = (job.delivery_results || []).length;
          return (
            <div
              key={job.id}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(170px, 1.3fr) minmax(150px, 1fr) auto",
                gap: 14,
                alignItems: "center",
                padding: "10px 4px",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {job.sales_user_name_snapshot || `Sales user ${job.sales_user_id}`}
                </div>
                <div style={{ fontSize: 11, color: "#8c8c8c" }}>{formatScheduledTime(job.scheduled_for)}</div>
              </div>
              <div>
                <div style={{ display: "flex", gap: 4, marginBottom: 3 }}>
                  {(job.formats || []).map(format => (
                    <Tag key={format} style={{ marginInlineEnd: 0 }}>{format.toUpperCase()}</Tag>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: job.last_error ? "#cf1322" : "#8c8c8c" }}>
                  {job.last_error || `${successful} of ${total} deliveries`}
                </div>
              </div>
              <Tag color={statusInfo.color} style={{ marginInlineEnd: 0 }}>{statusInfo.label}</Tag>
            </div>
          );
        })}
      </div>

      {(pagination.total || 0) > pagination.page_size && (
        <Pagination
          size="small"
          current={pagination.page}
          pageSize={pagination.page_size}
          total={pagination.total}
          showSizeChanger={false}
          style={{ paddingTop: 8, alignSelf: "flex-end" }}
          onChange={page => onLoad(page, status)}
        />
      )}
    </div>
  );
}

export function WaitingPriceReportSchedulerPanel() {
  const [messageApi, messageContext] = message.useMessage();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [availableOwnerTypes, setAvailableOwnerTypes] = useState(DEFAULT_OWNER_USER_TYPES);
  const [usersLoading, setUsersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("configuration");
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsStatus, setJobsStatus] = useState("");
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 0 });

  const loadApprovedUsers = useCallback(async (ownerTypes, notifyOnError = true) => {
    setUsersLoading(true);
    try {
      const users = await fetchWaitingPriceReportUsers([...ownerTypes, "Purchaser"]);
      setApprovedUsers(users || []);
      return users || [];
    } catch (error) {
      if (notifyOnError) messageApi.error(getErrorMessage(error, "Failed to load approved users."));
      throw error;
    } finally {
      setUsersLoading(false);
    }
  }, [messageApi]);

  const loadConfiguration = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [data, userTypeRows] = await Promise.all([
        fetchWaitingPriceReportConfig(),
        fetchWaitingPriceReportUserTypes(),
      ]);
      const registeredTypes = (userTypeRows || []).map(row => row.user_type).filter(Boolean);
      const rawConfig = { ...DEFAULT_CONFIG, ...(data.config || {}) };
      const nextConfig = {
        ...rawConfig,
        allowedOwnerUserTypes: canonicalizeOwnerTypes(rawConfig.allowedOwnerUserTypes, registeredTypes),
      };
      setAvailableOwnerTypes(
        [...new Set(registeredTypes)]
          .filter(userType => userType !== "Purchaser")
          .sort((a, b) => a.localeCompare(b))
      );
      setConfig(nextConfig);
      await loadApprovedUsers(nextConfig.allowedOwnerUserTypes, false);
    } catch (error) {
      setLoadError(getErrorMessage(error, "Failed to load the scheduler configuration."));
    } finally {
      setLoading(false);
    }
  }, [loadApprovedUsers]);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const loadJobs = useCallback(async (page = 1, status = jobsStatus) => {
    setJobsLoading(true);
    try {
      const data = await fetchWaitingPriceReportJobs({ page, pageSize: 20, status });
      setJobs(data.jobs || []);
      setPagination(data.pagination || { page, page_size: 20, total: 0, total_pages: 0 });
    } catch (error) {
      messageApi.error(getErrorMessage(error, "Failed to load recent report jobs."));
    } finally {
      setJobsLoading(false);
    }
  }, [jobsStatus, messageApi]);

  const save = useCallback(async () => {
    const error = validateConfig(config);
    if (error) {
      messageApi.error(error);
      return;
    }
    const payload = {
      enabled: config.enabled,
      allowedOwnerUserTypes: config.allowedOwnerUserTypes,
      salesUserIds: config.salesUserIds,
      purchaserUserIds: config.purchaserUserIds,
      times: [...config.times].sort(),
      intervalMinutes: config.intervalMinutes,
      formats: config.formats,
    };
    setSaving(true);
    try {
      const data = await saveWaitingPriceReportConfig(payload);
      setConfig(current => ({ ...current, ...(data.config || payload) }));
      messageApi.success(data.config?.enabled ? "Waiting-price schedule saved." : "Waiting-price schedule disabled.");
    } catch (error) {
      messageApi.error(getErrorMessage(error, "Failed to save the scheduler configuration."));
    } finally {
      setSaving(false);
    }
  }, [config, messageApi]);

  const salesUsers = useMemo(
    () => approvedUsers.filter(user => config.allowedOwnerUserTypes.includes(user.user_type)),
    [approvedUsers, config.allowedOwnerUserTypes]
  );
  const purchasers = useMemo(
    () => approvedUsers.filter(user => user.user_type === "Purchaser"),
    [approvedUsers]
  );

  const changeAllowedOwnerTypes = useCallback((allowedOwnerUserTypes) => {
    const allowed = new Set(allowedOwnerUserTypes);
    setConfig(current => ({
      ...current,
      allowedOwnerUserTypes,
      salesUserIds: current.salesUserIds.filter(id => {
        const selectedUser = approvedUsers.find(user => Number(user.id) === Number(id));
        return !selectedUser || allowed.has(selectedUser.user_type);
      }),
    }));
    loadApprovedUsers(allowedOwnerUserTypes).catch(() => {});
  }, [approvedUsers, loadApprovedUsers]);

  const tabs = useMemo(() => ([
    {
      key: "configuration",
      label: "Configuration",
      children: (
        <ConfigurationPane
          config={config}
          setConfig={setConfig}
          salesUsers={salesUsers}
          purchasers={purchasers}
          availableOwnerTypes={availableOwnerTypes}
          usersLoading={usersLoading}
          saving={saving}
          onSave={save}
          onAllowedTypesChange={changeAllowedOwnerTypes}
        />
      ),
    },
    {
      key: "jobs",
      label: "Recent deliveries",
      children: (
        <JobsPane
          jobs={jobs}
          pagination={pagination}
          loading={jobsLoading}
          status={jobsStatus}
          setStatus={setJobsStatus}
          onLoad={loadJobs}
        />
      ),
    },
  ]), [
    availableOwnerTypes,
    changeAllowedOwnerTypes,
    config,
    jobs,
    jobsLoading,
    jobsStatus,
    loadJobs,
    pagination,
    purchasers,
    salesUsers,
    save,
    saving,
    usersLoading,
  ]);

  if (loading) return <Skeleton active paragraph={{ rows: 7 }} />;
  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Scheduler configuration unavailable"
        description={loadError}
        action={<Button size="small" onClick={loadConfiguration}>Retry</Button>}
      />
    );
  }

  return (
    <>
      {messageContext}
      <Tabs
        activeKey={activeTab}
        items={tabs}
        onChange={key => {
          setActiveTab(key);
          if (key === "jobs" && !jobs.length) loadJobs(1, jobsStatus);
        }}
      />
    </>
  );
}
